import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, combineLatest, of, forkJoin } from "rxjs";
import { map, shareReplay, catchError, startWith, switchMap, filter, distinctUntilChanged } from "rxjs/operators";
import {
  StatsFilters,
  StatsData,
  FilteredStats,
  AvailableFilters,
  StatsInsights,
  ModelStat,
  FileStat,
  FieldStat,
  AccuracyRangeOption,
  ModelStatusCounts,
  EvaluationOutcome,
  FileInsightItem,
  FieldInsightItem,
  CompletedRunCache,
} from "../models/stats-dashboard.model";
import { DashboardResponse, GroundTruthStatusType, AccuracyStatsResponse, EvalSummaryResponse } from "../models/truth-management.model";
import { AuthService } from "./auth.service";
import { environment } from "../../environments/environment";

@Injectable()
export class StatsService {
  // State management
  private filtersSubject: BehaviorSubject<StatsFilters>;
  public filters$: Observable<StatsFilters>;

  // Raw data from API
  public rawStats$: Observable<StatsData>;
  private rawDashboard$: Observable<any>;

  // Derived observables
  public filteredStats$: Observable<FilteredStats>;
  public availableFilters$: Observable<AvailableFilters>;
  public insights$: Observable<StatsInsights>;

  // New observables for redesigned dashboard
  public modelStatusCounts$: Observable<ModelStatusCounts>;
  public evaluationOutcome$: Observable<EvaluationOutcome>;
  public isDataLoaded$: Observable<boolean>;
  
  private reloadTrigger$ = new BehaviorSubject<void>(undefined);

  // Cache for completed run data (persisted in sessionStorage)
  private completedRunsCache: Map<string, CompletedRunCache> = new Map();
  private readonly CACHE_STORAGE_KEY = 'stats-completed-runs-cache';
  
  // Cache for per-client evaluation summary and accuracy stats
  private evalSummaryCache = new Map<string, Observable<EvalSummaryResponse>>();
  private accuracyStatsCache = new Map<string, Observable<AccuracyStatsResponse>>();

  // API base URL
  private apiUrl = `${environment.apiBaseUrl}/api/v1`;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.loadCacheFromStorage();

    const savedFilters = sessionStorage.getItem('stats-dashboard-filters');
    let initialFilters: StatsFilters;

    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      initialFilters = {
        selectedModels: parsed.selectedModels || [],
        selectedFiles: parsed.selectedFiles || [],
        selectedFields: parsed.selectedFields || [],
        accuracyRanges: parsed.accuracyRanges || [],
      };
    } else {
      initialFilters = {
        selectedModels: [],
        selectedFiles: [],
        selectedFields: [],
        accuracyRanges: [],
      };
    }

    this.filtersSubject = new BehaviorSubject<StatsFilters>(initialFilters);
    this.filters$ = this.filtersSubject.asObservable();

    // Clear filters when client switches
    this.authService.currentClient$.pipe(
      distinctUntilChanged((prev, curr) => prev?.client_id === curr?.client_id)
    ).subscribe((client) => {
      this.resetFilters();
    });

    // Load data from two APIs: /eval/summary + /eval/accuracy-stats
    // Wait for currentClient$ to emit before making first API call
    const combinedData$ = this.authService.currentClient$.pipe(
      filter(client => client !== null), // Wait for first non-null client
      switchMap(() => {
        // Combine currentClient$ with reloadTrigger$ to handle both initial load and subsequent reloads
        return combineLatest([
          this.authService.currentClient$,
          this.reloadTrigger$
        ]).pipe(
          switchMap(([currentClient]) => {
            // Get current clientId from AuthService
            const clientId = currentClient?.client_id;
            return forkJoin({
              summary: this.getEvalSummary(clientId),
              stats: this.getAccuracyStats(clientId),
            }).pipe(
              map(({ summary, stats }) => this.mergeSummaryAndStats(summary, stats)),
              catchError((error) => {
                return of({ document_models: [] } as DashboardResponse);
              })
            );
          })
        );
      }),
      shareReplay(1)
    );

    this.rawDashboard$ = combinedData$.pipe(
      startWith({ document_models: [] } as DashboardResponse),
      shareReplay(1)
    );

    this.isDataLoaded$ = combinedData$.pipe(
      map(() => true),
      startWith(false),
      shareReplay(1)
    );

    // Load data once with error handling
    this.rawStats$ = this.rawDashboard$.pipe(
      map((dashboardResponse) => this.transformDashboardToStats(dashboardResponse)),
      shareReplay(1)
    );

    // Compute filtered stats
    this.filteredStats$ = combineLatest([this.rawStats$, this.filters$]).pipe(
      map(([stats, filters]) => this.computeFilteredStats(stats, filters)),
      shareReplay(1)
    );

    // Compute available filters based on selected models (cascading)
    this.availableFilters$ = combineLatest([this.rawStats$, this.filters$]).pipe(
      map(([stats, filters]) => this.computeAvailableFilters(stats, filters)),
      shareReplay(1)
    );

    // Compute insights - uses api-provided data directly
    this.insights$ = this.filteredStats$.pipe(
      map((stats) => this.computeInsights(stats)),
      shareReplay(1)
    );

    // Compute model status counts
    this.modelStatusCounts$ = this.rawDashboard$.pipe(
      map((dashboard) => this.computeModelStatusCounts(dashboard)),
      shareReplay(1)
    );

    // Compute evaluation outcome
    this.evaluationOutcome$ = combineLatest([this.rawDashboard$, this.filters$]).pipe(
      map(([dashboard, filters]) => this.computeEvaluationOutcome(dashboard, filters)),
      shareReplay(1)
    );

    // Save filters on change
    this.filters$.subscribe((filters) => {
      sessionStorage.setItem('stats-dashboard-filters', JSON.stringify(filters));
    });
  }

  // ==================== PUBLIC API ====================

  public updateFilters(partialFilters: Partial<StatsFilters>): void {
    const currentFilters = this.filtersSubject.value;
    this.filtersSubject.next({ ...currentFilters, ...partialFilters });
  }

  public resetFilters(): void {
    this.filtersSubject.next({
      selectedModels: [],
      selectedFiles: [],
      selectedFields: [],
      accuracyRanges: [],
    });
  }

  public getCurrentFilters(): StatsFilters {
    return this.filtersSubject.value;
  }

  public reload(): void {
    this.reloadTrigger$.next();
  }

  public clearCache(): void {
    this.completedRunsCache.clear();
    sessionStorage.removeItem(this.CACHE_STORAGE_KEY);
  }

  // ==================== API METHODS ====================

  /**
   * GET /eval/summary - Get evaluation summary for all document models
   * Results are cached per-client to support client switching.
   * @param clientId - Optional client ID to filter by specific client
   */
  private getEvalSummary(clientId?: string): Observable<EvalSummaryResponse> {
    const cacheKey = clientId || 'default';
    
    // Return cached result if available
    if (this.evalSummaryCache.has(cacheKey)) {
      return this.evalSummaryCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/eval/summary`;
    let params = new HttpParams();
    
    if (clientId) {
      params = params.set('client_id', clientId);
    }
    
    // Make HTTP request with caching
    const request$ = this.http.get<EvalSummaryResponse>(url, { params: params.keys().length > 0 ? params : undefined }).pipe(
      catchError((error) => {
        return of({ document_models: [] } as EvalSummaryResponse);
      }),
      shareReplay(1) // Cache the result for concurrent subscribers
    );
    
    // Store in cache
    this.evalSummaryCache.set(cacheKey, request$);
    return request$;
  }

  /**
   * GET /eval/accuracy-stats - Get accuracy statistics with top/bottom files and fields
   * Results are cached per-client to support client switching.
   * @param clientId - Optional client ID to filter by specific client
   */
  getAccuracyStats(clientId?: string): Observable<AccuracyStatsResponse> {
    const cacheKey = clientId || 'default';
    
    // Return cached result if available
    if (this.accuracyStatsCache.has(cacheKey)) {
      return this.accuracyStatsCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/eval/accuracy-stats`;
    let params = new HttpParams();
    
    if (clientId) {
      params = params.set('client_id', clientId);
    }
    
    // Make HTTP request with caching
    const request$ = this.http.get<AccuracyStatsResponse>(url, { params: params.keys().length > 0 ? params : undefined }).pipe(
      catchError((error) => {
        return of({ document_models: [] } as AccuracyStatsResponse);
      }),
      shareReplay(1) // Cache the result for concurrent subscribers
    );
    
    // Store in cache
    this.accuracyStatsCache.set(cacheKey, request$);
    return request$;
  }

  // ==================== CACHE MANAGEMENT ====================

  private loadCacheFromStorage(): void {
    try {
      const cached = sessionStorage.getItem(this.CACHE_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        this.completedRunsCache = new Map(Object.entries(parsed));
      }
    } catch (error) {
      this.completedRunsCache.clear();
    }
  }

  private saveCacheToStorage(): void {
    try {
      const cacheObj = Object.fromEntries(this.completedRunsCache);
      sessionStorage.setItem(this.CACHE_STORAGE_KEY, JSON.stringify(cacheObj));
    } catch (error) {
    }
  }

  private getCachedRun(documentCode: string): CompletedRunCache | undefined {
    return this.completedRunsCache.get(documentCode);
  }

  private setCachedRun(documentCode: string, cache: CompletedRunCache): void {
    this.completedRunsCache.set(documentCode, cache);
    this.saveCacheToStorage();
  }

  // ==================== ACCURACY FORMATTING ====================

  /**
   * Format accuracy value to 2 decimal places
   * Input: 46.9999 or 0.469999 (decimal form)
   * Output: 46.99 or 46.9999 (based on input precision)
   */
  private formatAccuracy(value: number): string {
    if (value === undefined || value === null || isNaN(value)) {
      return '0.00';
    }
    // If value is in 0-1 range (decimal), convert to percentage
    const percentValue = value <= 1 ? value * 100 : value;
    return percentValue.toFixed(2);
  }

  /**
   * Keep full decimal precision internally, format only for display
   */
  private normalizeAccuracy(value: number): number {
    if (value === undefined || value === null || isNaN(value)) {
      return 0;
    }
    // Convert 0-1 range to 0-100 range
    return value <= 1 ? value * 100 : value;
  }

  // ==================== DATA LOADING ====================

 private transformDashboardToStats(dashboard: any): StatsData {
  const models: ModelStat[] = [];
  const files: FileStat[] = [];
  const fields: FieldStat[] = [];

  if (!dashboard?.document_models) {
    return this.getEmptyStats();
  }

  dashboard.document_models.forEach((docModel: any) => {
    const modelName = docModel.document_code;

    // Add most accurate files from accuracy-stats API
    if (docModel._most_accurate_files) {
      docModel._most_accurate_files.forEach((f: any) => {
        files.push({
          fileName: f.file_name,
          model: modelName,
          accuracy: this.normalizeAccuracy(f.accuracy),
          totalFields: 0,
          matchedFields: 0,
          fileId: f.file_id,
        });
      });
    }

    // Add least accurate files from accuracy-stats API
    if (docModel._least_accurate_files) {
      docModel._least_accurate_files.forEach((f: any) => {
        files.push({
          fileName: f.file_name,
          model: modelName,
          accuracy: this.normalizeAccuracy(f.accuracy),
          totalFields: 0,
          matchedFields: 0,
          fileId: f.file_id,
        });
      });
    }

    // Add least accurate fields from accuracy-stats API
    if (docModel._least_accurate_fields) {
      docModel._least_accurate_fields.forEach((field: any) => {
        fields.push({
          fieldName: field.field_name,
          model: modelName,
          accuracy: this.normalizeAccuracy(field.accuracy),
          matched: false,
          matchedCount: 0,
          totalCount: 1,
          score: 0,
        });
      });
    }

    // Build model stats
    const modelAccuracy = this.normalizeAccuracy(docModel._accuracy || 0);
    const evalFilesCount = docModel.eval_files_count || 0;
    const schemaFieldCount = docModel.key_count || 0;

    models.push({
      modelName,
      totalFiles: evalFilesCount,
      totalFields: schemaFieldCount,
      averageAccuracy: modelAccuracy,
    });
  });

  const totalFiles = dashboard.document_models.reduce(
    (sum: number, m: any) => sum + (m.eval_files_count || 0),
    0
  );

  const totalFields = dashboard.document_models.reduce(
    (sum: number, m: any) => sum + (m.key_count || 0),
    0
  );

  return {
    models,
    files,
    fields,
    overallAccuracy: 0,
    totalModels: models.length,
    totalFiles,
    totalFields,
  };
}
  // ==================== FILTERING ====================

  private computeFilteredStats(stats: StatsData, filters: StatsFilters): FilteredStats {
    let filteredModels = stats.models;
    if (filters.selectedModels.length > 0) {
      filteredModels = stats.models.filter((m) =>
        filters.selectedModels.includes(m.modelName)
      );
    }

    if (filters.accuracyRanges.length > 0) {
      filteredModels = this.filterModelsByAccuracyRanges(filteredModels, filters.accuracyRanges);
    }

    const filteredModelNames = filteredModels.map(m => m.modelName);

    let filteredFiles = stats.files;
    if (filteredModelNames.length > 0) {
      filteredFiles = filteredFiles.filter((f) =>
        filteredModelNames.includes(f.model)
      );
    }
    if (filters.selectedFiles.length > 0) {
      filteredFiles = filteredFiles.filter((f) =>
        filters.selectedFiles.includes(f.fileName)
      );
    }

    let filteredFields = stats.fields;
    if (filteredModelNames.length > 0) {
      filteredFields = filteredFields.filter((f) =>
        filteredModelNames.includes(f.model)
      );
    }
    if (filters.selectedFields.length > 0) {
      filteredFields = filteredFields.filter((f) =>
        filters.selectedFields.includes(f.fieldName)
      );
    }

    if (filters.accuracyRanges.length > 0) {
      filteredFields = this.filterByAccuracyRanges(filteredFields, filters.accuracyRanges);
    }

    const avgMatchedSum = filteredFields.reduce((sum, f) => sum + f.matchedCount, 0);
    const avgTotalSum = filteredFields.reduce((sum, f) => sum + f.totalCount, 0);
    const averageAccuracy = avgTotalSum > 0 ? this.normalizeAccuracy(avgMatchedSum / avgTotalSum) : 0;

    return {
      models: filteredModels,
      files: filteredFiles,
      fields: filteredFields,
      averageAccuracy,
      totalModels: filteredModels.length,
      totalFiles: filteredModels.reduce((sum, m) => sum + m.totalFiles, 0),
      totalFields: filteredModels.reduce((sum, m) => sum + m.totalFields, 0),
    };
  }

  private filterByAccuracyRanges(fields: FieldStat[], ranges: AccuracyRangeOption[]): FieldStat[] {
    if (ranges.length === 0) return fields;
    return fields.filter((f) => {
      return ranges.some(range => {
        switch (range) {
          case "0-40": return f.accuracy >= 0 && f.accuracy < 40;
          case "40-70": return f.accuracy >= 40 && f.accuracy < 70;
          case "70-80": return f.accuracy >= 70 && f.accuracy < 80;
          case "80-90": return f.accuracy >= 80 && f.accuracy < 90;
          case "90-100": return f.accuracy >= 90 && f.accuracy <= 100;
        }
      });
    });
  }

  private filterModelsByAccuracyRanges(models: ModelStat[], ranges: AccuracyRangeOption[]): ModelStat[] {
    if (ranges.length === 0) return models;
    return models.filter((m) => {
      return ranges.some(range => {
        switch (range) {
          case "0-40": return m.averageAccuracy >= 0 && m.averageAccuracy < 40;
          case "40-70": return m.averageAccuracy >= 40 && m.averageAccuracy < 70;
          case "70-80": return m.averageAccuracy >= 70 && m.averageAccuracy < 80;
          case "80-90": return m.averageAccuracy >= 80 && m.averageAccuracy < 90;
          case "90-100": return m.averageAccuracy >= 90 && m.averageAccuracy <= 100;
        }
      });
    });
  }

  // ==================== CASCADING FILTERS ====================

  private computeAvailableFilters(stats: StatsData, filters: StatsFilters): AvailableFilters {
    const availableModels = Array.from(new Set(stats.models.map((m) => m.modelName)));

    let availableFiles = stats.files;
    if (filters.selectedModels.length > 0) {
      availableFiles = stats.files.filter((f) =>
        filters.selectedModels.includes(f.model)
      );
    }

    let availableFields = stats.fields;
    if (filters.selectedModels.length > 0) {
      availableFields = stats.fields.filter((f) =>
        filters.selectedModels.includes(f.model)
      );
    }

    return {
      models: availableModels,
      files: Array.from(new Set(availableFiles.map((f) => f.fileName))),
      fields: Array.from(new Set(availableFields.map((f) => f.fieldName))),
    };
  }

  // ==================== INSIGHTS ====================

  /**
   * Compute insights using pre-computed data from accuracy-stats API
   * The backend now provides most_accurate_files, least_accurate_files, and least_accurate_fields
   */
  private computeInsights(stats: FilteredStats): StatsInsights {
    const topAccurate: FileInsightItem[] = [];
    const leastAccurate: FileInsightItem[] = [];
    const leastAccurateFields: FieldInsightItem[] = [];

    // Group files by model and merge with API insights data
    const filesByModel = new Map<string, FileStat[]>();
    stats.files.forEach(file => {
      if (!filesByModel.has(file.model)) {
        filesByModel.set(file.model, []);
      }
      filesByModel.get(file.model)!.push(file);
    });

    // Get top 10 accurate files across all filtered models
    const allFiles = Array.from(filesByModel.values()).flat();
    const sortedFiles = allFiles.sort((a, b) => b.accuracy - a.accuracy);
    sortedFiles.slice(0, 10).forEach(f => {
      topAccurate.push({
        fileName: f.fileName,
        model: f.model,
        accuracy: f.accuracy,
        fileId: f.fileId,
      });
    });

    // Get bottom 10 least accurate files
    sortedFiles.slice(-10).reverse().forEach(f => {
      leastAccurate.push({
        fileName: f.fileName,
        model: f.model,
        accuracy: f.accuracy,
        fileId: f.fileId,
      });
    });

    // Get bottom 10 least accurate fields
    const sortedFields = [...stats.fields].sort((a, b) => a.accuracy - b.accuracy);
    sortedFields.slice(0, 10).forEach(f => {
      leastAccurateFields.push({
        fieldName: f.fieldName,
        model: f.model,
        accuracy: f.accuracy,
        matchedCount: f.matchedCount,
        totalCount: f.totalCount,
      });
    });

    return {
      topAccurate,
      leastAccurate,
      leastAccurateFields,
    };
  }

  // ==================== HELPERS ====================

  private getEmptyStats(): StatsData {
    return {
      models: [],
      files: [],
      fields: [],
      overallAccuracy: 0,
      totalModels: 0,
      totalFiles: 0,
      totalFields: 0,
    };
  }

  // ==================== MODEL STATUS COUNTS ====================

  private computeModelStatusCounts(dashboard: DashboardResponse): ModelStatusCounts {
    const counts: ModelStatusCounts = {
      HEALTHY: 0,
      NO_SAMPLE: 0,
      FIELD_MISSING: 0,
      FIELD_UPDATE: 0,
      total: 0,
    };

    if (!dashboard?.document_models) {
      return counts;
    }

    dashboard.document_models.forEach((docModel: any) => {
      const status = docModel._status as GroundTruthStatusType;
      if (status && counts.hasOwnProperty(status)) {
        counts[status]++;
        counts.total++;
      }
    });

    return counts;
  }

  // ==================== EVALUATION OUTCOME ====================

private computeEvaluationOutcome(dashboard: any, filters: StatsFilters): EvaluationOutcome {
  const outcome: EvaluationOutcome = {
    totalFiles: 0,
    successFiles: 0,
    failedFiles: 0,
    overallAccuracy: 0,
    totalFields: 0,
    matchedFields: 0,
    unmatchedFields: 0,
    passRate: 0,
  };

  // Access document_models array from the merged dashboard object
  if (!dashboard?.document_models || dashboard.document_models.length === 0) {
    return outcome;
  }

  let modelsToAggregate = dashboard.document_models;
  
  // Apply model filter if selected
  if (filters.selectedModels.length > 0) {
    modelsToAggregate = modelsToAggregate.filter((m: any) =>
      filters.selectedModels.includes(m.document_code)
    );
  }

  let totalAccuracySum = 0;
  let modelsWithData = 0;

  modelsToAggregate.forEach((docModel: any) => {
    // Get accuracy from _accuracy field (populated by mergeSummaryAndStats from summary API)
    if (docModel._accuracy !== null && docModel._accuracy !== undefined) {
      totalAccuracySum += this.normalizeAccuracy(docModel._accuracy);
      modelsWithData++;
    }

    // Aggregate matched/unmatched fields from summary API
    outcome.matchedFields += docModel._matched_fields ?? 0;
    outcome.totalFields += (docModel._matched_fields ?? 0) + (docModel._unmatched_fields ?? 0);
    
    // Aggregate passed/failed files from accuracy-stats API
    outcome.successFiles += docModel._total_passed_files ?? 0;
    outcome.failedFiles += docModel._total_failed_files ?? 0;
  });

  // Calculate final values
  if (modelsWithData > 0) {
    outcome.overallAccuracy = totalAccuracySum / modelsWithData;
  }
  
  outcome.unmatchedFields = outcome.totalFields - outcome.matchedFields;
  outcome.totalFiles = outcome.successFiles + outcome.failedFiles;
  
  // Calculate pass rate
  if (outcome.totalFiles > 0) {
    outcome.passRate = (outcome.successFiles / outcome.totalFiles) * 100;
  }

  return outcome;
}
  // ==================== DATA MERGING ====================

  private mergeSummaryAndStats(
  summary: EvalSummaryResponse,
  stats: AccuracyStatsResponse
): any {
  if (!summary?.document_models) {
    return { document_models: [] };
  }

  const statsMap = new Map<string, any>(
    (stats?.document_models || []).map(m => [m.document_model, m])
  );

  const merged = summary.document_models.map((summaryModel: any) => {
    const statsModel = statsMap.get(summaryModel.document_model);

    return {
      document_code: summaryModel.document_model,
      document_name: summaryModel.document_model,
      key_count: summaryModel.attributes_count,
      eval_files_count: summaryModel.files_count,
      // Store accuracy stats directly (from accuracy-stats API)
      _most_accurate_files: statsModel?.most_accurate_files || [],
      _least_accurate_files: statsModel?.least_accurate_files || [],
      _least_accurate_fields: statsModel?.least_accurate_fields || [],
      _total_passed_files: statsModel?.total_passed_files ?? 0,
      _total_failed_files: statsModel?.total_failed_files ?? 0,
      // Summary metrics
      _status: summaryModel.status,
      _accuracy: summaryModel.accuracy,
      _matched_fields: summaryModel.matched_fields ?? 0,
      _unmatched_fields: summaryModel.unmatched_fields ?? 0,
    };
  });

  return { document_models: merged };
}

}